import React from 'react';

// utils
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_LONG } from '../../util/types';
import { convertCategoriesToSelectTreeOptions, constructQueryParamName } from '../../util/search';

// component imports
import SelectSingleFilter from './SelectSingleFilter/SelectSingleFilter';
import SelectMultipleFilter from './SelectMultipleFilter/SelectMultipleFilter';
import KeywordFilter from './KeywordFilter/KeywordFilter';
import PriceFilter from './PriceFilter/PriceFilter';
import IntegerRangeFilter from './IntegerRangeFilter/IntegerRangeFilter';

const formatLabelWithCount = (label, count, fallbackValue, showCounts = true) => {
  const baseLabel =
    typeof label === 'string' && label.length > 0
      ? label
      : typeof fallbackValue === 'string' && fallbackValue.length > 0
      ? fallbackValue
      : `${fallbackValue ?? ''}`;
  const safeCount = typeof count === 'number' && Number.isFinite(count) ? count : 0;
  return showCounts ? `${baseLabel} (${safeCount})` : baseLabel;
};

const addCountsToFlatOptions = (options = [], counts = {}, showCounts = true) => {
  return options
    .map(option => {
      const optionValue = option?.option;
      const valueKey = optionValue != null ? `${optionValue}` : '';
      const count = counts?.[valueKey] || 0;
      return {
        ...option,
        label: formatLabelWithCount(option.label, count, valueKey, showCounts),
        __count: count,
      };
    })
    .filter(option => option.__count > 0)
    .sort((a, b) => b.__count - a.__count)
    .map(option => {
      const { __count, ...rest } = option;
      return rest;
    });
};

const mapTreeOptionsWithCounts = (options = [], counts = {}, showAllOptions = false, showCounts = true) => {
  return options
    .map(option => {
      const optionValue = option?.option;
      const valueKey = optionValue != null ? `${optionValue}` : '';
      const count = counts?.[valueKey] || 0;
      if (Array.isArray(option?.suboptions) && option.suboptions.length > 0) {
        const childOptions = mapTreeOptionsWithCounts(option.suboptions, counts, showAllOptions, showCounts);
        const filteredChildren = showAllOptions 
          ? childOptions 
          : childOptions.filter(child => child.__count > 0);
        const childrenTotal = filteredChildren.reduce((sum, child) => sum + child.__count, 0);
        const totalCount = childrenTotal || count;
        return {
          ...option,
          label: formatLabelWithCount(option.label, totalCount, valueKey, showCounts),
          suboptions: filteredChildren,
          __count: totalCount,
        };
      }

      return {
        ...option,
        label: formatLabelWithCount(option.label, count, valueKey, showCounts),
        suboptions: [],
        __count: count,
      };
    })
    .filter(option => showAllOptions || option.__count > 0)
    .sort((a, b) => b.__count - a.__count);
};

const stripCountsFromTree = (options = []) =>
  options.map(option => {
    const { __count, suboptions = [], ...rest } = option;
    const strippedSuboptions = stripCountsFromTree(suboptions);
    return strippedSuboptions.length > 0
      ? { ...rest, suboptions: strippedSuboptions }
      : rest;
  });

const addCountsToTreeOptions = (options = [], counts = {}, showAllOptions = false, showCounts = true) => {
  const withCounts = mapTreeOptionsWithCounts(options, counts, showAllOptions, showCounts);
  return stripCountsFromTree(withCounts);
};

/**
 * FilterComponent is used to map configured filter types
 * to actual filter components
 */
const FilterComponent = props => {
  const {
    idPrefix,
    config,
    urlQueryParams,
    initialValues,
    getHandleChangedValueFn,
    listingCategories,
    marketplaceCurrency,
    filterOptionCounts = {},
    intl,
    ...rest
  } = props;
  // Note: config can be either
  // - listingFields config or
  // - default filter config
  // They both have 'key' and 'schemaType' included.
  const { key, schemaType } = config;
  const { liveEdit, showAsPopup } = rest;
  const countsForFilter = filterOptionCounts?.[key] || {};
  const showCounts = props.showCounts !== false;

  const useHistoryPush = liveEdit || showAsPopup;
  const prefix = idPrefix || 'SearchPage';
  const componentId = `${prefix}.${key.toLowerCase()}`;
  const name = key.replace(/\s+/g, '-');

  // Default filters: price, keywords
  switch (schemaType) {
    case 'category': {
      const { scope, isNestedEnum, nestedParams } = config;
      const queryParamNames = nestedParams?.map(p => constructQueryParamName(p, scope));
      const baseOptions = convertCategoriesToSelectTreeOptions(listingCategories);
      // Show all category options even if count is 0
      const optionsWithCounts = addCountsToTreeOptions(baseOptions, countsForFilter, true, showCounts);
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.categoryLabel' })}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          isNestedEnum={isNestedEnum}
          {...rest}
        />
      );
    }
    case 'listingType': {
      const { scope, options } = config;
      const paramNames = [constructQueryParamName(key, scope)];
      const optionsWithCounts = addCountsToFlatOptions(options, countsForFilter, showCounts);
      if (optionsWithCounts.length === 0) {
        return null;
      }
      return (
        <SelectSingleFilter
          id={componentId}
          name={key}
          label={intl.formatMessage({ id: 'FilterComponent.listingTypeLabel' })}
          queryParamNames={[paramNames]}
          initialValues={initialValues(paramNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          {...rest}
        />
      );
    }
    case 'price': {
      const { min, max, step } = config;
      return (
        <PriceFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.priceLabel' })}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={min}
          max={max}
          step={step}
          marketplaceCurrency={marketplaceCurrency}
          {...rest}
        />
      );
    }
    case 'keywords':
      return (
        <KeywordFilter
          id={componentId}
          label={intl.formatMessage({ id: 'FilterComponent.keywordsLabel' })}
          name={name}
          queryParamNames={[key]}
          initialValues={initialValues([key], liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          {...rest}
        />
      );
  }

  // Custom extended data filters
  switch (schemaType) {
    case SCHEMA_TYPE_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, filterType, searchMode } = filterConfig;
      const effectiveSearchMode = searchMode || 'has_any';
      const queryParamNames = [constructQueryParamName(key, scope)];
      const optionsWithCounts = addCountsToFlatOptions(enumOptions, countsForFilter, showCounts);
      if (optionsWithCounts.length === 0) {
        return null;
      }
      return filterType === 'SelectSingleFilter' ? (
        <SelectSingleFilter
          id={componentId}
          label={label}
          name={name}
          // If this enum is the public 'brand' filter, pass both internal and public param names
          queryParamNames={
            key === 'brand' && scope !== 'meta'
              ? [...queryParamNames, 'brand']
              : queryParamNames
          }
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          isNestedEnum={false}
          {...rest}
        />
      ) : (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          schemaType={schemaType}
          searchMode={effectiveSearchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_MULTI_ENUM: {
      const { scope, enumOptions, filterConfig = {} } = config;
      const { label, searchMode } = filterConfig;
      const effectiveSearchMode = searchMode || 'has_any';
      const queryParamNames = [constructQueryParamName(key, scope)];
      const optionsWithCounts = addCountsToFlatOptions(enumOptions, countsForFilter, showCounts);
      if (optionsWithCounts.length === 0) {
        return null;
      }
      return (
        <SelectMultipleFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          options={optionsWithCounts}
          schemaType={schemaType}
          searchMode={effectiveSearchMode}
          {...rest}
        />
      );
    }
    case SCHEMA_TYPE_LONG: {
      const { minimum, maximum, scope, step, filterConfig = {} } = config;
      const { label } = filterConfig;
      const queryParamNames = [constructQueryParamName(key, scope)];
      return (
        <IntegerRangeFilter
          id={componentId}
          label={label}
          name={name}
          queryParamNames={queryParamNames}
          initialValues={initialValues(queryParamNames, liveEdit)}
          onSubmit={getHandleChangedValueFn(useHistoryPush)}
          min={minimum}
          max={maximum}
          step={step}
          {...rest}
        />
      );
    }
    default:
      return null;
  }
};

export default FilterComponent;
